import React, { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase/firebase-config";
import { FaInstagram, FaLinkedinIn, FaQuoteLeft, FaTags } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { IoGlobeOutline } from "react-icons/io5";
import {
  Avatar,
  Badge,
  Box,
  HStack,
  Icon,
  Image,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  SkeletonCircle,
  Tag,
  TagLabel,
  Text,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { socialLinks } from "../../lib/profileSocials";

const SOCIAL_ICONS = {
  instagram: FaInstagram,
  x: FaXTwitter,
  linkedin: FaLinkedinIn,
  website: IoGlobeOutline,
};

const InterestIcon = ({ icon, name }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  return (
    <Box mr={1.5} display="flex" alignItems="center">
      <SkeletonCircle size="16px" isLoaded={isLoaded} fadeDuration={0.4}>
        <Image
          src={icon}
          alt={name}
          boxSize="16px"
          objectFit="contain"
          onLoad={() => setIsLoaded(true)}
          borderRadius="full"
        />
      </SkeletonCircle>
    </Box>
  );
};

const TAG_COLORS = [
  { bg: "blue.50", color: "blue.600", borderColor: "blue.200" },
  { bg: "purple.50", color: "purple.600", borderColor: "purple.200" },
  { bg: "teal.50", color: "teal.600", borderColor: "teal.200" },
  { bg: "orange.50", color: "orange.600", borderColor: "orange.200" },
  { bg: "pink.50", color: "pink.600", borderColor: "pink.200" },
  { bg: "green.50", color: "green.600", borderColor: "green.200" },
  { bg: "cyan.50", color: "cyan.600", borderColor: "cyan.200" },
];

// The profile card opened from "Hosted by" and from a guest on the list.
export const HostInfoModal = ({ isOpen, onClose, host, showHostedBy = true }) => {
  const [interestsData, setInterestsData] = useState([]);
  const [loadingInterests, setLoadingInterests] = useState(true);

  const interestRefs = host?.interests || [];

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    const run = async () => {
      setLoadingInterests(true);
      if (!Array.isArray(interestRefs) || interestRefs.length === 0) {
        if (!cancelled) {
          setInterestsData([]);
          setLoadingInterests(false);
        }
        return;
      }
      try {
        const results = await Promise.all(
          interestRefs.map(async (ref) => {
            if (!ref?.id) return null;
            const snap = await getDoc(doc(db, "interests", ref.id));
            return snap.exists() ? { id: snap.id, ...snap.data() } : null;
          })
        );
        if (!cancelled) setInterestsData(results.filter(Boolean));
      } catch (err) {
        console.error("Error fetching profile interests:", err);
        if (!cancelled) setInterestsData([]);
      } finally {
        if (!cancelled) setLoadingInterests(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [isOpen, interestRefs]);

  const hasBio = !!(host?.bio && host.bio.trim());
  const links = socialLinks(host?.socials);
  const name = host?.displayName || "Unknown";

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      size="md"
      motionPreset="slideInBottom"
      scrollBehavior="inside"
    >
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(6px)" />
      <ModalContent borderRadius="20px" overflow="hidden" mx={4} my={6} maxH="calc(100vh - 48px)">
        <ModalHeader pb={2} pt={5} px={6} borderBottom="1px solid" borderColor="gray.100">
          <HStack spacing={3} align="center">
            <Avatar
              size="2xl"
              name={name}
              src={host?.avatarUrl || host?.photoURL}
              border="3px solid"
              borderColor="blue.100"
            />
            <Box minW={0} flex={1}>
              <Text fontSize="lg" fontWeight="700" color="gray.800" noOfLines={1}>
                {name}
              </Text>
              {showHostedBy && (
                <Text fontSize="xs" color="gray.400">
                  Hosted by · {name}
                </Text>
              )}
              {links.length > 0 && (
                <HStack spacing={1.5} mt={2}>
                  {links.map((link) => (
                    <Link
                      key={link.id}
                      href={link.href}
                      isExternal
                      aria-label={link.label}
                      w="28px"
                      h="28px"
                      borderRadius="full"
                      border="1px solid"
                      borderColor="gray.200"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      color="gray.600"
                      _hover={{ borderColor: "#508cf7", color: "#508cf7" }}
                    >
                      <Icon as={SOCIAL_ICONS[link.id]} boxSize="13px" />
                    </Link>
                  ))}
                </HStack>
              )}
            </Box>
          </HStack>
          <ModalCloseButton top={4} right={4} />
        </ModalHeader>

        <ModalBody px={6} py={5}>
          <VStack align="stretch" spacing={6}>
            {hasBio ? (
              <Box
                bg="gray.50"
                borderRadius="14px"
                p={4}
                border="1px solid"
                borderColor="gray.100"
                position="relative"
              >
                <Icon as={FaQuoteLeft} color="blue.200" boxSize={3} position="absolute" top={3} left={3} />
                <Text fontSize="sm" color="gray.700" lineHeight="1.6" pl={6} whiteSpace="pre-wrap">
                  {host.bio}
                </Text>
              </Box>
            ) : (
              <Text fontSize="sm" color="gray.400" fontStyle="italic" textAlign="center">
                No bio yet.
              </Text>
            )}

            <Box>
              <HStack spacing={2} mb={3}>
                <Icon as={FaTags} color="gray.400" boxSize={3} />
                <Text fontSize="11px" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="wide">
                  Interests
                </Text>
                {!loadingInterests && (
                  <Badge colorScheme="blue" fontSize="10px" borderRadius="full" px={2}>
                    {interestsData.length}
                  </Badge>
                )}
              </HStack>

              {loadingInterests ? (
                <HStack spacing={2}>
                  <Skeleton height="28px" width="80px" borderRadius="full" />
                  <Skeleton height="28px" width="100px" borderRadius="full" />
                  <Skeleton height="28px" width="70px" borderRadius="full" />
                </HStack>
              ) : interestsData.length === 0 ? (
                <Text fontSize="sm" color="gray.400" fontStyle="italic">
                  No interests selected.
                </Text>
              ) : (
                <Wrap spacing={2}>
                  {interestsData.map((interest, index) => {
                    const colorScheme = TAG_COLORS[index % TAG_COLORS.length];
                    return (
                      <WrapItem key={interest.id}>
                        <Tag
                          size="md"
                          borderRadius="full"
                          bg={colorScheme.bg}
                          color={colorScheme.color}
                          border="1px solid"
                          borderColor={colorScheme.borderColor}
                          px={3}
                          py={1.5}
                          fontWeight="500"
                        >
                          {interest.icon && <InterestIcon icon={interest.icon} name={interest.name} />}
                          <TagLabel fontSize="xs">{interest.name}</TagLabel>
                        </Tag>
                      </WrapItem>
                    );
                  })}
                </Wrap>
              )}
            </Box>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
